import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Spin, Table } from 'antd'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import BaseListing from 'src/BaseListings'
import { wtFormatMoney } from 'src/helpers/commons/utils'
import WidgetDepartmentSelector from 'src/widgets/Selectors/WidgetDepartmentSelector'
import { CButton, CCol, CCard, CCardBody, CInputGroup, CForm, CRow } from '@coreui/react'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { Link } from 'react-router-dom'

const EmployeeSalariesReport = () => {
  const Base = new BaseListing()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [reportData, setReportData] = useState([])
  const [allowanceTypes, setAllowanceTypes] = useState([])
  const [deductionTypes, setDeductionTypes] = useState([])
  const [dynamicCols, setDynamicCols] = useState([])
  const [departments, setDepartments] = useState([])
  const [branches, setBranches] = useState([])
  const [department_id, setDepartmentId] = useState(0)
  const [department_name, setDepartmentName] = useState('')
  const [branchId, setBranchId] = useState(0)
  const [branch_name, setBranchName] = useState('')


  const staticColumns = [
    {
      title: <b>Employee Info</b>,
      children: [
        {
          title: 'Sr #',
          key: 'sr_no',
          render: (_, __, index) => index + 1,
          width: 50,
          fixed: 'left',
        },
        {
          title: 'Employee Id',
          dataIndex: ['employee', 'id'], // or 'employee_id' depending on your data structure
          key: 'employee_id',
          fixed: 'left',
          width: 100,
          render: (text, record) => (
            <Link to={`/employee-profile?id=${record.employee.id}`} target="_blank" rel="noopener noreferrer">
              {record.employee.id}
            </Link>
          ),
        },
        {
          title: 'Employee Name',
          dataIndex: ['employee', 'full_name'],
          key: 'employee_name',
          fixed: 'left',
          width: 150,
          render: (text, record) => (
            <Link to={`/employee-profile?id=${record.employee.id}`} target="_blank" rel="noopener noreferrer">
              {record.employee.full_name}
            </Link>
          ),
        },
        {
          title: 'Department',
          dataIndex: ['employee', 'department', 'name'],
          key: 'department_name',
          fixed: 'left',
          width: 150,
        }

      ],
    },
  ]

  const changeValues = (key, val) => {
    if (key === 'department_id') {
      setDepartmentId(val.value)
      setDepartmentName(val.label)
    }

    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
  }

  const getReportData = () => {
    setIsLoading(true)
    wtApiCall(
      'reports/employee_salaries_report',
      {
        branch_id: branchId,
        department_id: department_id,
      },
      'get',
      (response) => {
        const salaryData = response.data?.employee_salaries || []
        const allowanceTypes = response.data?.salary_allowance_types || []
        const deductionTypes = response.data?.salary_deduction_types || []

        setAllowanceTypes(allowanceTypes)
        setDeductionTypes(deductionTypes)

        // Normalize each salary record for easier column-based rendering
        const normalized = salaryData.map((salary) => {
          const allowanceMap = {}
          const deductionMap = {}

          salary.allowances?.forEach((a) => {
            allowanceMap[a.allowance_type_id] = parseFloat(a.amount)
          })

          salary.deductions?.forEach((d) => {
            deductionMap[d.deduction_type_id] = parseFloat(d.amount)
          })

          return {
            ...salary,
            basic_salary: parseFloat(salary.basic_salary),
            gross_salary: parseFloat(salary.gross_salary),
            total_salary: parseFloat(salary.total_salary),
            allowance_map: allowanceMap,
            deduction_map: deductionMap,
          }
        })

        setReportData(normalized)
        generateDynamicColumns(allowanceTypes, deductionTypes)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const generateDynamicColumns = (allowanceTypes, deductionTypes) => {
    const salaryCols = [
      {
        title: 'Basic Salary',
        dataIndex: 'basic_salary',
        key: 'basic_salary',
        align: 'left',
        render: (value) => wtFormatMoney(value, '', false, 0),
      },
      ...allowanceTypes.map((type) => ({
        title: type.name,
        key: `allowance_${type.id}`,
        align: 'left',
        render: (record) => wtFormatMoney(record.allowance_map?.[type.id] || 0, '', false, 0),
      })),
      ...deductionTypes.map((type) => ({
        title: type.name,
        key: `deduction_${type.id}`,
        align: 'left',
        render: (record) => wtFormatMoney(record.deduction_map?.[type.id] || 0, '', false, 0),
      })),
      {
        title: 'Gross Salary',
        dataIndex: 'gross_salary',
        key: 'gross_salary',
        align: 'left',
        render: (value) => wtFormatMoney(value, '', false, 0),
      },
    ]

    setDynamicCols([
      ...staticColumns,
      {
        title: <b>Salary Breakdown</b>,
        children: salaryCols,
      },
    ])
  }

  useEffect(() => {
    Base.dropdownData(
      'branches,departments',
      {},
      [setBranches, setDepartments],
      1,
    )
  }, [])

  return (
    <div className="container-fluid" style={{ padding: '1em', backgroundColor: 'white' }}>
      <CForm layout="vertical" form={form}>
        <Card title={<h2 style={{ textAlign: 'center' }}>Employee Salaries Report</h2>}>
          <CRow style={{ width: '100%' }}>
            <CCol md={12}>
              <CCard className="card-dark">
                <CCardBody style={{ backgroundColor: '#f7f9fa' }}>
                  <br />
                  <CRow>
                    {/* <CCol md={1}></CCol> */}
                    <CCol md={1}>
                      <CInputGroup>
                        <label style={{ fontSize: '140%', marginTop: '50px' }}>Filters:</label>
                      </CInputGroup>
                    </CCol>

                    <CCol md={3}>
                      <CInputGroup>
                        <label>Department</label>
                        <WidgetDepartmentSelector
                          departments={departments}
                          displayValue={department_name}
                          value={department_id}
                          placeholder="Select Department"
                          onChange={(values) => changeValues('department_id', values)}
                          reloadDropdownData={() =>
                            Base.dropdownData('departments', {}, [setDepartments], 1)
                          }
                        />
                      </CInputGroup>
                    </CCol>

                    <CCol md={3}>
                      <CInputGroup>
                        <label>Branch</label>
                        <WidgetBranchSelector
                          branches={branches}
                          displayValue={branch_name}
                          value={branchId}
                          placeholder="Select Branch"
                          onChange={(values) => changeValues('branch_id', values)}
                          reloadDropdownData={() =>
                            Base.dropdownData('branches', {}, [setBranches], 1)
                          }
                        />
                      </CInputGroup>
                    </CCol>
                    <CCol md={4}>

                      <CButton
                        color="primary"
                        onClick={() => getReportData()}
                        style={{ marginTop: '37px', marginLeft: '1%' }}
                      >
                        Search
                      </CButton>
                      {/* {Base.hasRights('Export Employee Salaries Report') && (
                        <CButton
                          color="success"
                          onClick={onDownload}
                          style={{ marginBottom: '20px', marginTop: '20px', marginLeft: '1%' }}
                          className="text-white"
                        >
                          Export
                        </CButton>
                      )} */}


                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
          <br></br>
          <Table
            columns={dynamicCols}
            dataSource={reportData}
            rowKey="id"
            bordered
            size="small"
            loading={{ indicator: <Spin />, spinning: isLoading }}
            scroll={{ x: 'max-content', y: 600 }}
            pagination={false}
          />
        </Card>
      </CForm>
    </div>
  )
}

export default EmployeeSalariesReport
